import { useQuery } from '@apollo/client'
import AppBar from '@material-ui/core/AppBar'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import { useTheme } from '@material-ui/core/styles'
import { useMemo, useState } from 'react'
import { Redirect, useParams } from 'react-router-dom'

import { buildFeatureSet, canAccessTab, getCredentials } from '../../cache/auth'
import Breadcrumbs from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import { CHANNEL_NAME_QUERY } from '../../graphql/channel'
import { TabPanel } from '../../helpers/TabPanel'
import {
  SupportDistributionSuggestion,
  useRegisterSupportScreen
} from '../../helpers/supportScreenContext'
import { getTranslate, lang } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import channelPagesRoleManagement from '../../staticRoleManagement/channelPagesRoleManagement'
import { Channel } from '../../types/channel'
import ChannelAPIkeys from '../ChannelAPIKeys'
import ChannelForm from '../ChannelForm'
import { ChannelMapping } from '../ChannelMapping'
import ChannelPush from '../ChannelPush'
import { UploadBoxForChannel } from '../ChannelUpload'
import useStyles from './styles/index.styles'

export default function ChannelTabs() {
  const classes = useStyles()
  const theme = useTheme()
  const { id, channelCode, page } = useParams<{
    id: string
    channelCode: string
    page: string
  }>()
  const [supportMapping, setSupportMapping] =
    useState<SupportDistributionSuggestion>()
  const { data, loading, error } = useQuery<{
    channelName: Channel
  }>(CHANNEL_NAME_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      channelId: id
    }
  })
  useRegisterSupportScreen({
    relatedDistribution:
      !loading &&
      !error &&
      page === 'mapping' &&
      supportMapping?.refType === 'CHANNEL_MAPPING' &&
      supportMapping.channelCode ===
        data?.channelName?.basicDetails.displayChannelCode
        ? [supportMapping]
        : [],
    affectedEntities:
      !loading && !error && data?.channelName?.basicDetails
        ? [
            {
              type: 'channel',
              code: data.channelName.basicDetails.channelCode,
              label: data.channelName.basicDetails.name
            }
          ]
        : []
  })
  const cachedLevels = useMemo(
    () => [
      { label: getTranslate('Channels'), link: '/channels' },
      {
        label: data?.channelName.basicDetails.name || ''
      }
    ],
    [data]
  )

  const tabsList = [
    {
      label: getTranslate('Details'),
      value: 'details',
      link: `/channels/${id}/${channelCode}/details`,
      component: <ChannelForm />,
      dataCy: 'DetailsTab'
    },
    {
      label: getTranslate('Logo'),
      value: 'logo',
      link: `/channels/${id}/${channelCode}/logo`,
      component: <UploadBoxForChannel />,
      dataCy: 'LogoTab'
    },
    {
      label: getTranslate('Mapping'),
      value: 'mapping',
      link: `/channels/${id}/${channelCode}/mapping`,
      component: (
        <ChannelMapping
          channelCode={
            !loading && !error
              ? data?.channelName.basicDetails.displayChannelCode || ''
              : ''
          }
          onSupportMappingChange={setSupportMapping}
        />
      ),
      dataCy: 'MappingTab'
    },
    {
      label: getTranslate('API Keys'),
      value: 'api-keys',
      link: `/channels/${id}/${channelCode}/api-keys`,
      component: <ChannelAPIkeys cachedLevels={cachedLevels} />,
      dataCy: 'APIKeyTab'
    },
    {
      label: getTranslate('ARI Push'),
      value: 'push',
      link: `/channels/${id}/${channelCode}/push`,
      component: <ChannelPush />,
      dataCy: 'PushTab'
    }
  ]

  const credentials = getCredentials()
  const featureSet = buildFeatureSet(credentials)

  const routeConfig = channelPagesRoleManagement.find(
    (item) => item.route === '/channels/:id/:channelCode/:page'
  )

  const visibleTabs = tabsList.filter((tab) =>
    canAccessTab(routeConfig, tab.value, featureSet)
  )

  if (!canAccessTab(routeConfig, page, featureSet)) {
    return <Redirect to="/403" />
  }

  return (
    <div className={classes.root}>
      <PageHeader
        title={
          lang === 'fa'
            ? data?.channelName.basicDetails.name
            : // TODO: handle english name
              data?.channelName.basicDetails.name
        }
      />
      <AppBar position="static" color="transparent">
        <Breadcrumbs levels={cachedLevels} loading={loading} />{' '}
        <Tabs
          className={classes.tabs}
          value={page}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto">
          {visibleTabs.map((tabItem, index) => (
            <Tab
              key={tabItem.value + index}
              data-cy={tabItem.dataCy}
              value={tabItem.value}
              className={classes.tab}
              label={tabItem.label}
              component={LocalizedLink}
              to={tabItem.link}
            />
          ))}
        </Tabs>
      </AppBar>
      {visibleTabs.map((tabItem) => (
        <TabPanel
          key={tabItem.value}
          value={page}
          index={tabItem.value}
          dir={theme.direction}
          className={classes.tabPanel}>
          {tabItem.component}
        </TabPanel>
      ))}
    </div>
  )
}
